import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  GraduationCap, Briefcase, Shield, Trees, Award, TrendingUp,
  Dumbbell, Train, Users, Compass, Wallet, Maximize2,
  Navigation, LayoutGrid, Palette, Star, Target, Fingerprint,
  Search, MessageSquare, Share2, RefreshCw, ArrowDown,
  FileDown, Cross, CheckCircle, XCircle, AlertCircle, MinusCircle, Scale, BarChart3
} from "lucide-react";
import { API } from "../App";

const ICON_MAP = {
  GraduationCap, Briefcase, Shield, Trees, Award, TrendingUp,
  Dumbbell, Train, Users, Compass, Wallet, Maximize: Maximize2,
  Navigation, Cross, LayoutGrid, Palette,
};

const MhMReportPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [sessRes, cfgRes] = await Promise.all([
          axios.get(`${API}/mhm/sessions/${sessionId}`),
          axios.get(`${API}/mhm/config`),
        ]);
        setSession(sessRes.data);
        setConfig(cfgRes.data);

        if (sessRes.data.buyer_report) {
          setReport(sessRes.data.buyer_report);
        } else if (sessRes.data.fingerprint) {
          const repRes = await axios.post(`${API}/mhm/sessions/${sessionId}/generate-report`);
          setReport(repRes.data);
        }

        // Set document title for PDF filename
        const d = new Date(sessRes.data.created_at);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        document.title = `MhMClarityReport_${dd}_${mm}_${yy}`;
      } catch (err) {
        setError(err.response?.status === 404 ? "Report not found" : "Failed to load report");
      } finally { setLoading(false); }
    };
    init();
    return () => { document.title = "PropertyCRM"; };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#87A96B] mx-auto" />
          <p className="mt-4 text-slate-500 text-sm">Loading your MhM Clarity Report...</p>
        </div>
      </div>
    );
  }

  if (error || !session?.fingerprint) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-full bg-[#E8F0E0] flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-[#87A96B]" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Report Not Available</h2>
          <p className="text-sm text-slate-500 mb-4">
            {error === "Report not found"
              ? "This report link may have expired or the session no longer exists."
              : "The assessment hasn't been completed yet."}
          </p>
          <a href="/mhm/start" className="inline-flex items-center gap-2 bg-[#87A96B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Take MhM Assessment
          </a>
        </div>
      </div>
    );
  }

  const fp = session.fingerprint;
  const fMap = {};
  const fIconMap = {};
  config?.factors?.forEach((f) => {
    const k = f.key || f.factor_id;
    fMap[k] = { business: f.businessLabel || f.name, buyer: f.buyerLabelV3 || f.buyerLabelV2 || f.buyerLabel || f.name, icon: f.icon };
    fIconMap[k] = f.icon;
  });
  const pMap = {};
  config?.personas?.forEach((p) => { pMap[p.key || p.persona_id] = p; });
  const persona = pMap[session.persona_id] || {};

  const elimNames = (session.eliminated_factors || []).map((k) => fMap[k]?.buyer || k);
  const highNames = (session.high_priorities || []).map((k) => fMap[k]?.buyer || k);
  const medNames = (session.medium_priorities || []).map((k) => fMap[k]?.buyer || k);
  const lowNames = (session.low_priorities || []).map((k) => fMap[k]?.buyer || k);

  const topFactors = fp.ranked_factors?.filter((rf) => rf.weight > 0).slice(0, 3) || [];
  const reportId = `MHM-CR-${new Date(session.created_at).getFullYear()}-${sessionId.split("_")[1]?.slice(0, 6) || "000000"}`.toUpperCase();
  const dateStr = new Date(session.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const bandColor = (fk) => {
    if (session.eliminated_factors?.includes(fk)) return "#64748b";
    if (session.high_priorities?.includes(fk)) return "#16a34a";
    if (session.medium_priorities?.includes(fk)) return "#ea580c";
    return "#2563eb";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="mhm-report-page">
      {/* Download Button — sticky bottom on mobile, fixed top-right on desktop */}
      <div className="print:hidden fixed bottom-0 left-0 right-0 md:bottom-auto md:left-auto md:top-4 md:right-4 z-50 md:w-auto">
        <button onClick={() => window.print()} className="w-full md:w-auto bg-[#87A96B] text-white px-4 py-3 md:py-1.5 md:rounded-lg shadow-lg flex items-center justify-center gap-2 hover:bg-[#769A5B] transition-colors text-sm md:text-sm font-medium">
          <FileDown className="w-4 h-4" /> Download PDF
        </button>
      </div>

      <div className="max-w-[850px] mx-auto px-4 md:px-6 py-4 md:py-6 pb-16 md:pb-6 print:px-4 print:py-2 print:max-w-none">

        {/* ========== HEADER ========== */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between border-b-2 border-[#87A96B] pb-4 mb-5 rpt-no-break">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-gradient-to-br from-[#87A96B] to-[#6B9155] flex items-center justify-center flex-shrink-0">
              <div className="text-white text-center leading-none">
                <p className="text-[7px] font-medium">My</p>
                <p className="text-sm font-black">MhM</p>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">YOUR MhM CLARITY REPORT</h1>
              <p className="text-sm text-slate-600 font-medium">Your Home Priorities. Your Decision Guide.</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Based on your MyHomeMatrix home clarity assessment.</p>
            </div>
          </div>
          <div className="text-left md:text-right text-[10px] text-[#8B8580] mt-2 md:mt-0 flex gap-3 md:block md:space-y-0.5 flex-shrink-0 md:ml-4">
            <p><span className="font-semibold text-slate-700">REPORT ID</span> {reportId}</p>
            <p><span className="font-semibold text-slate-700">DATE</span> {dateStr}</p>
          </div>
        </div>

        {/* ========== PERSONA BAR ========== */}
        <div className="bg-gradient-to-r from-[#F0F5EC] to-[#EAF0E4] rounded-xl p-4 mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rpt-no-break">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#87A96B] flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-[#87A96B] font-semibold uppercase tracking-wider">Your Situation</p>
              <p className="text-base font-bold text-slate-900">{persona.buyerLabelV2 || persona.businessLabel || persona.name || session.persona_id}</p>
              <p className="text-[10px] text-slate-500">{persona.descriptionV2 || persona.description || ""}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:gap-5">
            {[
              { icon: CheckCircle, label: "Assessment Completed" },
              { icon: Target, label: "10 Factors Analysed" },
              { icon: Fingerprint, label: "Clarity Profile Ready" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <item.icon className="w-6 h-6 text-[#87A96B]" />
                <p className="text-[10px] text-slate-600 font-medium leading-tight">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ========== SECTION 1: PRIORITY BLUEPRINT ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-0.5">1. YOUR PRIORITY MAP</h2>
          <p className="text-[10px] text-slate-500 mb-3">Here's how your home priorities are organised based on your choices.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[
              { title: "TOP PRIORITIES", count: highNames.length, items: highNames, color: "green", icon: Star, note: "These are your strongest decision drivers. Don't compromise here.", iconFill: true },
              { title: "IMPORTANT TO YOU", count: medNames.length, items: medNames, color: "amber", icon: MinusCircle, note: "These still matter and support your decision." },
              { title: "LOWER PRIORITIES", count: lowNames.length, items: lowNames, color: "blue", icon: ArrowDown, note: "These have some weight, but are less likely to drive your final call." },
              { title: "MORE FLEXIBLE", count: elimNames.length, items: elimNames, color: "slate", icon: XCircle, note: "You are comfortable being flexible on these." },
            ].map((col) => (
              <div key={col.title} className={`border-2 border-${col.color}-200 rounded-lg p-3 bg-${col.color}-50/30`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <col.icon className={`w-4 h-4 text-${col.color}-500 ${col.iconFill ? `fill-${col.color}-500` : ""}`} />
                  <span className={`text-[11px] font-bold text-${col.color}-600`}>{col.title} ({col.count})</span>
                </div>
                <p className="text-[9px] text-slate-500 mb-2">{col.title === "TOP PRIORITIES" ? "Things you would be least comfortable compromising on." : col.title === "IMPORTANT TO YOU" ? "These matter, but are not your strongest drivers." : col.title === "LOWER PRIORITIES" ? "These are lower on your list relative to others." : "These are less likely to become deal-breakers for you."}</p>
                <ol className="space-y-1 mb-2">
                  {col.items.map((n, i) => <li key={i} className={`text-[10px] text-slate-700 ${col.title === "HIGH PRIORITY" ? "font-medium" : ""}`}>{i + 1}. {n}</li>)}
                </ol>
                <p className={`text-[9px] text-${col.color}-600 italic bg-${col.color}-50 rounded px-1.5 py-1`}>{col.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-3 bg-[#F0F5EC] rounded-lg p-2.5 flex items-start gap-2">
            <Star className="w-3.5 h-3.5 text-[#87A96B] mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-[#4A4543]">
              You chose {highNames.length} top priorities, {medNames.length} important factors, {lowNames.length} lower priorities, and {elimNames.length} areas where you can be more flexible.
              <span className="font-semibold"> This shows a clear and thoughtful approach to your decision.</span>
            </p>
          </div>
        </div>

        {/* ========== SECTION 2: PRIORITY WEIGHTAGE ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-0.5">2. HOW YOUR PRIORITIES WEIGH UP</h2>
          <p className="text-[10px] text-slate-400 mb-3">The relative importance of each factor based on your choices.</p>
          <div className="space-y-1">
            {fp.ranked_factors?.map((rf) => {
              const pct = (rf.weight / 10) * 100;
              const color = bandColor(rf.factor_id);
              return (
                <div key={rf.factor_id} className="flex items-center gap-2">
                  <span className="text-[9px] text-slate-600 w-[140px] text-right flex-shrink-0 truncate">{fMap[rf.factor_id]?.buyer || rf.buyerLabel || rf.name}</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  <span className="text-[10px] font-bold text-slate-700 w-7 text-right">{rf.weight}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-2">
            {[{l: "Top Priority", c: "#16a34a"}, {l: "Important", c: "#ea580c"}, {l: "Lower", c: "#2563eb"}, {l: "Flexible", c: "#64748b"}].map((b) => (
              <div key={b.l} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.c }} />
                <span className="text-[9px] text-slate-500">{b.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ========== SECTION 3: TOP 3 DRIVERS ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-3">3. YOUR TOP 3 DECISION DRIVERS</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topFactors.map((rf, i) => {
              const Icon = ICON_MAP[fIconMap[rf.factor_id]] || Star;
              const colors = [
                "border-green-300 bg-green-50/50 text-[#5A7A4A]",
                "border-[#87A96B] bg-[#F0F5EC]/50 text-[#4A4543]",
                "border-emerald-300 bg-emerald-50/50 text-emerald-800"
              ];
              return (
                <div key={rf.factor_id} className={`flex items-center gap-3 p-3.5 rounded-xl border-2 ${colors[i]}`}>
                  <div className="w-11 h-11 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <span className="text-xl font-black">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold">{fMap[rf.factor_id]?.buyer || rf.buyerLabel || rf.name}</p>
                    <p className="text-[10px] opacity-70">Weight: {rf.weight}/10</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========== SECTION 4: TRADE-OFF STYLE ========== */}
        {report?.tradeoff_insight && (
          <div className="mb-5 rpt-no-break">
            <h2 className="text-sm font-black text-slate-900 mb-2">4. YOUR TRADE-OFF STYLE</h2>
            <div className="bg-slate-50 rounded-xl p-4 border">
              <div className="flex items-center gap-10 justify-center mb-3">
                <div className="text-center">
                  <CheckCircle className="w-7 h-7 text-[#87A96B] mx-auto" />
                  <p className="text-[10px] text-[#5A7A4A] mt-1 font-medium">What you prioritize</p>
                </div>
                <Scale className="w-5 h-5 text-slate-300" />
                <div className="text-center">
                  <XCircle className="w-7 h-7 text-slate-300 mx-auto" />
                  <p className="text-[10px] text-slate-400 mt-1 font-medium">What you trade away</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-700 text-center leading-relaxed">{report.tradeoff_insight}</p>
            </div>
          </div>
        )}

        {/* ========== SECTION 5: WHAT THIS MEANS ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-3">5. WHAT THIS MEANS FOR YOU</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { icon: CheckCircle, color: "green", title: "Your Top Priorities", text: report?.top_priorities },
              { icon: AlertCircle, color: "amber", title: "Important — Supporting Factors", text: report?.important_priorities || report?.medium_priorities },
              { icon: ArrowDown, color: "blue", title: "Flexible — Willing to Compromise", text: report?.flexible_priorities || report?.low_priorities },
              { icon: XCircle, color: "slate", title: "Not Decision Drivers — Off the Table", text: report?.not_decision_drivers || report?.eliminated_explanation },
            ].filter((c) => c.text).map((card, i) => (
              <div key={i} className="flex gap-2.5 items-start p-3 rounded-lg bg-slate-50 border">
                <card.icon className={`w-5 h-5 text-${card.color}-500 mt-0.5 flex-shrink-0`} />
                <div>
                  <p className={`text-[11px] font-bold text-${card.color}-700 mb-0.5`}>{card.title}</p>
                  <p className="text-[10px] text-slate-600 leading-relaxed">{card.text}</p>
                </div>
              </div>
            ))}
          </div>
          {report?.persona_summary && (
            <div className="mt-3 bg-[#F0F5EC]/50 rounded-lg p-3 border-l-4 border-[#87A96B]">
              <p className="text-[11px] text-slate-700 italic leading-relaxed">"{report.persona_summary}"</p>
            </div>
          )}
        </div>

        {/* ========== SECTION 6: HOW TO USE + FOOTER ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-3">6. WHAT YOU CAN DO NEXT</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[
              { icon: Search, title: "Compare homes", desc: "Use your priorities to evaluate and compare homes you are considering." },
              { icon: MessageSquare, title: "Ask better questions", desc: "Focus your site visit conversations around what truly matters to you." },
              { icon: Share2, title: "Share with your advisor", desc: "Help them understand what you need so they can show you better options." },
              { icon: RefreshCw, title: "Revisit anytime", desc: "As your needs evolve, take this again to see how your priorities shift." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 bg-[#F0F5EC]/50 rounded-lg border border-[#D4E0CC]">
                <item.icon className="w-4 h-4 text-[#87A96B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold text-slate-800">{item.title}</p>
                  <p className="text-[9px] text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer CTA — inside the same no-break block */}
          <div className="bg-gradient-to-r from-[#4A4543] to-[#5B5550] rounded-xl p-5 mt-4 print:hidden">
            <div className="flex items-center gap-3 mb-3">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400 flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-bold">You now have clarity on what matters most.</p>
                <p className="text-[#C4B8AC] text-[11px]">Use this clarity to find a home that truly fits your life.</p>
              </div>
            </div>
            <a href={`${window.location.origin}/mhm/projects/${sessionId}`} target="_blank" rel="noopener noreferrer"
              data-testid="mhm-compare-homes-link"
              className="block bg-[#87A96B] hover:bg-[#76955d] rounded-lg p-4 text-center transition-colors mb-2"
              style={{ textDecoration: "none" }}>
              <p className="text-white text-sm font-bold">Compare the homes you're considering</p>
              <p className="text-white/80 text-[10px] mt-1">Add projects, rate each on your priorities, and see how they stack up</p>
            </a>
            <a href={`${window.location.origin}/mhm/next/${sessionId}`} target="_blank" rel="noopener noreferrer"
              data-testid="mhm-revisit-link"
              className="block bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition-colors"
              style={{ textDecoration: "none" }}>
              <p className="text-white text-xs font-semibold">Other options — revisit your priorities or start fresh</p>
            </a>
          </div>
        </div>

        <p className="text-center text-[8px] text-slate-300 mt-3 print:mt-2">Powered by MyHomeMatrix | PropertyCRM</p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @page { size: A4; margin: 10mm 12mm; }
          .rpt-no-break { page-break-inside: avoid; break-inside: avoid; }
          .rpt-page-break-before { page-break-before: always; break-before: page; }
        }
      `}</style>
    </div>
  );
};

export default MhMReportPage;
